import React, { useRef } from 'react';

import {
    Box, Button, Flex, Grid, Icon, IconButton,
    GridItem, NumberDecrementStepper, NumberIncrementStepper, NumberInput,
    NumberInputField, Input,
    NumberInputStepper, Slider, SliderFilledTrack, SliderMark, SliderThumb, SliderTrack, Switch, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useColorModeValue, Tooltip, Stack,
    Center, Tag, HStack,
    Spacer
} from "@chakra-ui/react";

import { FaRegQuestionCircle } from "react-icons/fa";
import { IoInformationCircleOutline } from "react-icons/io5";
import { FiDownload, FiTrash2, FiPlus } from "react-icons/fi";

import { useEffect, useState } from "react";

import { SchemeCanvas } from "./SchemeCanvas";
import { BoxSchemeCanvas3D } from "./BoxSchemeCanvas3D";
import { FaCube, FaPuzzlePiece } from "react-icons/fa";
import { Bambu } from "../icons/Bambu";

const defaultW = "160.0";
const defaultD = "70.0";
const defaultH = "95.0";
const defaultT = "3.0";
const defaultCard = true;
const defaultDividers = 8;
const defaultSegLen = 30;
const defaultKerf = 0;

const TitleWithTooltip = ({ title, hasTooltip, tooltipLabel }) => {
    return (
        <Flex direction="row" gap={2} alignItems="center" display="inline-flex">
            <Text lineHeight="short">{title}</Text>
            {hasTooltip && (
                <Tooltip label={tooltipLabel} shouldWrapChildren>
                    <Icon as={FaRegQuestionCircle} verticalAlign="middle" transform="translateY(-2px)" />
                </Tooltip>
            )}
        </Flex>
    );
};

const SliderControl = ({
    title,
    hasTooltip,
    tooltipLabel,
    stateHook,
    setStateHook,
    minValue,
    maxValue,
    valueStep,
    localStorageKey: _localStorageKey,
    sliderMarkCondition = false,
    sliderMarkValue = 0,
    noStepper = false,
    stepperDecimals = 1,
    unit,
    onCommit,
    canBeHidden = false,
    disabled = false
}) => {

    const faded = disabled ? 0.55 : 1;

    return <>
        <GridItem opacity={faded}>
            <TitleWithTooltip title={title} hasTooltip={hasTooltip} tooltipLabel={tooltipLabel} />
        </GridItem>
        <GridItem colSpan={noStepper ? 2 : 1} display={canBeHidden ? ["none", "block"] : "block"} opacity={faded}>
            <Slider isDisabled={disabled} value={Number(stateHook) || 0} min={minValue} max={maxValue} step={valueStep} focusThumbOnChange={false} onChange={(v) => {
                const formatted = Number.parseFloat(v).toFixed(stepperDecimals);
                setStateHook(formatted);
                onCommit && onCommit(formatted);
            }}>
                {sliderMarkCondition && <SliderMark value={sliderMarkValue} mt="-16px" ml="-2px" fontSize="xl" color="gray">{"|"}</SliderMark>}
                <SliderTrack><SliderFilledTrack /></SliderTrack><SliderThumb />
            </Slider>
        </GridItem>
        {!noStepper && <GridItem colSpan={canBeHidden ? [2, 1] : 1} opacity={faded}>
            <Flex direction="row" gap={2} alignItems="center">
                <NumberInput isDisabled={disabled} value={stateHook} min={minValue} max={maxValue} step={valueStep} onChange={(valueString) => {
                    setStateHook(valueString);
                }} onBlur={() => onCommit && onCommit(stateHook)} onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        onCommit && onCommit(stateHook);
                    }
                }}>
                    <NumberInputField />
                    <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                    </NumberInputStepper>
                </NumberInput>
                {(typeof unit === "string" || unit instanceof String) && unit !== "" && <Text>{unit}</Text>}
            </Flex>
        </GridItem>}
    </>;
};

const SwitchControl = ({ title, stateHook, setStateHook, localStorageKey }) => {
    return <><GridItem><Text>{title}</Text></GridItem>
        <GridItem colSpan={2}>
            <Switch isChecked={stateHook} onChange={(e) => {
                setStateHook(e.target.checked);
                localStorage.setItem(localStorageKey, e.target.checked);
            }} />
        </GridItem></>
};

const defaultPresets = [
    {
        name: "Arkham Horrors (Revised Core)",
        values: {
            width: 240,
            depth: 100,
            height: 72,
            thickness: 3,
            dividers: 9,
            isCardTray: true,
            segmentLength: 30,
            kerf: 0
        }
    },
    {
        name: "Bicycle Playing Cards",
        values: {
            width: 30,
            depth: 63,
            height: 97,
            thickness: 3,
            dividers: 0,
            isCardTray: true,
            segmentLength: 30,
            kerf: 0
        }
    },
    {
        name: "Pen Cup (2-Slot)",
        values: {
            width: 80,
            depth: 80,
            height: 100,
            thickness: 3,
            dividers: 1,
            isCardTray: false,
            segmentLength: 30,
            kerf: 0
        }
    },
    {
        name: "Universal Dice Tray",
        values: {
            width: 200,
            depth: 200,
            height: 30,
            thickness: 3,
            dividers: 0,
            isCardTray: false,
            segmentLength: 30,
            kerf: 0
        }
    },
    {
        name: "Uno",
        values: {
            width: 30,
            depth: 65,
            height: 95,
            thickness: 3,
            dividers: 0,
            isCardTray: true,
            segmentLength: 30,
            kerf: 0
        }
    }
];

export const BoxSchemeControl = ({ is3DMode = false, ...props }) => {

    const [width, setWidth] = useState(defaultW);
    const [depth, setDepth] = useState(defaultD);
    const [height, setHeight] = useState(defaultH);
    const [matThickness, setMatThickness] = useState(defaultT);
    const [isCardTray, setIsCardTray] = useState(defaultCard);
    const [dividers, setDividers] = useState(defaultDividers);
    const [segmentLength, setSegmentLength] = useState(defaultSegLen);
    const [kerf, setKerf] = useState(defaultKerf);
    const [presets, setPresets] = useState([]);
    const [newPresetName, setNewPresetName] = useState("");
    const [presetNameError, setPresetNameError] = useState(false);
    const lastValidRef = useRef({
        width: defaultW,
        depth: defaultD,
        height: defaultH,
        matThickness: defaultT,
        dividers: defaultDividers.toString(),
        segmentLength: defaultSegLen.toString(),
        kerf: defaultKerf.toString()
    });
    const setters = {
        width: setWidth,
        depth: setDepth,
        height: setHeight,
        matThickness: setMatThickness,
        dividers: setDividers,
        segmentLength: setSegmentLength,
        kerf: setKerf
    };
    const computeTags = (vals) => {
        const { width: w, depth: d, height: h, dividers: div, isCardTray: card } = vals;
        const tags = [];
        if (!card) {
            tags.push("box");
            const minDim = Math.min(w, d);
            if (h < 0.2 * minDim) tags.push("flat");
        } else {
            tags.push("cards");
            if (d > h * 1.2) tags.push("horizontal");
            if (h > d * 1.2) tags.push("vertical");
            tags.push(div <= 1 ? "fixed" : "adjustable");
        }
        return tags;
    };
    const commitValue = (key, rawValue, { min = 0, max = Number.POSITIVE_INFINITY, decimals = 1, integer = false, allowZero = false } = {}) => {
        const setter = setters[key];
        if (!setter) return;
        const parsed = integer ? Number.parseInt(rawValue, 10) : Number.parseFloat(rawValue);
        const lowerBound = allowZero ? min : Math.max(min, 0.0001);
        const isValid = Number.isFinite(parsed) && ((allowZero ? parsed >= lowerBound : parsed > lowerBound)) && parsed <= max;
        if (!isValid) {
            setter(lastValidRef.current[key]);
            return;
        }
        const clamped = Math.min(Math.max(parsed, lowerBound), max);
        const formatted = integer ? Math.round(clamped).toString() : clamped.toFixed(decimals);
        setter(formatted);
        lastValidRef.current[key] = formatted;
        localStorage.setItem(key, formatted);
    };

    const panelBg = useColorModeValue("rgba(255,255,255,0.9)", "rgba(0,0,0,0.9)");

    useEffect(() => {
        const stored = localStorage.getItem("boxPresets");
        if (stored === null) {
            localStorage.setItem("boxPresets", JSON.stringify(defaultPresets));
            setPresets(defaultPresets);
            return;
        }
        try {
            const parsed = JSON.parse(stored);
            if (!Array.isArray(parsed)) throw new Error("not array");
            const valid = [];
            parsed.forEach((item) => {
                try {
                    if (!item || typeof item.name !== "string" || item.name.trim() === "") throw new Error("bad name");
                    const vals = item.values || {};
                    const required = ["width", "depth", "height", "thickness", "dividers", "isCardTray", "segmentLength", "kerf"];
                    required.forEach((k) => {
                        if (vals[k] === undefined) throw new Error("missing " + k);
                    });
                    valid.push({
                        name: item.name,
                        values: {
                            width: Number(vals.width),
                            depth: Number(vals.depth),
                            height: Number(vals.height),
                            thickness: Number(vals.thickness),
                            dividers: Number(vals.dividers),
                            isCardTray: !!vals.isCardTray,
                            segmentLength: Number(vals.segmentLength),
                            kerf: Number(vals.kerf)
                        }
                    });
                } catch (e) {
                    // skip invalid
                }
            });
            const sorted = valid.sort((a, b) => a.name.localeCompare(b.name));
            setPresets(sorted);
            localStorage.setItem("boxPresets", JSON.stringify(sorted));
        } catch (e) {
            // parsing failed, reset to defaults
            localStorage.setItem("boxPresets", JSON.stringify(defaultPresets));
            setPresets(defaultPresets);
        }
    }, []);

    useEffect(() => {
        const readNumber = (key, defaultValue, { allowZero = false, integer = false } = {}) => {
            const stored = localStorage.getItem(key);
            const parsed = integer ? Number.parseInt(stored, 10) : Number.parseFloat(stored);
            const valid = Number.isFinite(parsed) && (allowZero ? parsed >= 0 : parsed > 0);
            return valid ? parsed.toString() : defaultValue.toString();
        };
        const w = readNumber("width", defaultW);
        const d = readNumber("depth", defaultD);
        const h = readNumber("height", defaultH);
        const t = readNumber("matThickness", defaultT);
        const div = readNumber("dividers", defaultDividers, { allowZero: true, integer: true });
        const seg = readNumber("segmentLength", defaultSegLen);
        const k = readNumber("kerf", defaultKerf, { allowZero: true });
        setWidth(w);
        setDepth(d);
        setHeight(h);
        setMatThickness(t);
        setDividers(div);
        setSegmentLength(seg);
        setKerf(k);
        lastValidRef.current = {
            ...lastValidRef.current,
            width: w,
            depth: d,
            height: h,
            matThickness: t,
            dividers: div,
            segmentLength: seg,
            kerf: k
        };
        const storedCardTray = localStorage.getItem("isCardTray");
        if (storedCardTray === "true" || storedCardTray === "false") {
            setIsCardTray(storedCardTray === "true");
        } else {
            setIsCardTray(true);
        }
    }, []);

    const safeParse = (val, fallback) => {
        const num = Number.parseFloat(val);
        return Number.isFinite(num) ? num : Number.parseFloat(fallback);
    };
    const safeParseInt = (val, fallback) => {
        const num = Number.parseInt(val);
        return Number.isFinite(num) ? num : Number.parseInt(fallback);
    };
    const numericWidth = safeParse(width, lastValidRef.current.width);
    const numericDepth = safeParse(depth, lastValidRef.current.depth);
    const numericHeight = safeParse(height, lastValidRef.current.height);
    const numericMatThickness = safeParse(matThickness, lastValidRef.current.matThickness);
    const numericDividers = safeParseInt(dividers, lastValidRef.current.dividers);
    const numericSegmentLength = safeParse(segmentLength, lastValidRef.current.segmentLength);
    const numericKerf = safeParse(kerf, lastValidRef.current.kerf);

    const {
        position = "fixed",
        left = "12px",
        right = ["12px", "12px"],
        bottom = ["12px"],
        w: controlWidth = ["calc(100vw - 24px)", "calc(100vw - 24px)", "560px"],
        ...restProps
    } = props;

    const [exportHandlers, setExportHandlers] = useState({});

    return (
        <>
            <SchemeCanvas
                width={numericWidth}
                depth={numericDepth}
                height={numericHeight}
                thickness={numericMatThickness}
                isCardTray={isCardTray}
                dividers={numericDividers}
                segmentLength={numericSegmentLength}
                kerf={numericKerf}
                hideCanvas={is3DMode}
                onExportHandlersReady={setExportHandlers}
            />
            {is3DMode &&
                <BoxSchemeCanvas3D
                    width={numericWidth}
                    depth={numericDepth}
                    height={numericHeight}
                    thickness={numericMatThickness}
                    dividers={numericDividers}
                    isCardTray={isCardTray}
                />}
            <Flex position={position} left={left} right={right} bottom={bottom} w={controlWidth} maxW="560px" flexDirection="column" gap={3} zIndex={3} {...restProps}>
                <Box boxShadow="lg" borderWidth="1px" borderRadius="lg" padding="20px" h="320px" background={panelBg} backdropFilter="auto" backdropBlur="5px">
                    <Tabs variant="solid-rounded" isLazy>
                        <TabList>
                            <Tab>Basic</Tab>
                            <Tab>Advanced</Tab>
                            <Spacer />
                            <Tab>Presets</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel>
                                <Grid templateColumns={["90px 1fr 130px", "90px 1fr 130px", "90px 220px 130px"]} templateRows="repeat(4, 50px)" rowGap={1} columnGap={6} alignItems="center">
                                    <SliderControl
                                        title="Panel Thickness"
                                        stateHook={matThickness}
                                        setStateHook={setMatThickness}
                                        minValue={0.5}
                                        maxValue={Math.min(Math.floor(10 * (width / (3 * dividers + 4))) / 10, 10, segmentLength / 2)}
                                        valueStep={0.1}
                                        localStorageKey="matThickness"
                                        sliderMarkCondition={Math.min(Math.floor(10 * (width / (3 * dividers + 4))) / 10, 10, segmentLength / 2) >= 3}
                                        sliderMarkValue={3}
                                        unit="mm"
                                        canBeHidden={true}
                                        onCommit={(val) => commitValue("matThickness", val, {
                                            min: 0.5,
                                            max: Math.min(Math.floor(10 * (width / (3 * dividers + 4))) / 10, 10, segmentLength / 2),
                                            decimals: 1
                                        })} />
                                    <SliderControl
                                        title="Box Width"
                                        stateHook={width}
                                        setStateHook={setWidth}
                                        minValue={matThickness * (3 * dividers + 4)}
                                        maxValue={350}
                                        valueStep={0.1}
                                        localStorageKey="width"
                                        sliderMarkCondition={false}
                                        unit="mm"
                                        canBeHidden={true}
                                        onCommit={(val) => commitValue("width", val, {
                                            min: matThickness * (3 * dividers + 4),
                                            max: 350,
                                            decimals: 1
                                        })} />
                                    <SliderControl
                                        title="Box Depth"
                                        stateHook={depth}
                                        setStateHook={setDepth}
                                        minValue={matThickness * 5}
                                        maxValue={350}
                                        valueStep={0.1}
                                        localStorageKey="depth"
                                        sliderMarkCondition={false}
                                        unit="mm"
                                        canBeHidden={true}
                                        onCommit={(val) => commitValue("depth", val, {
                                            min: matThickness * 5,
                                            max: 350,
                                            decimals: 1
                                        })} />
                                    <SliderControl
                                        title="Box Height"
                                        stateHook={height}
                                        setStateHook={setHeight}
                                        minValue={matThickness * 2}
                                        maxValue={200}
                                        valueStep={0.1}
                                        localStorageKey="height"
                                        sliderMarkCondition={false}
                                        unit="mm"
                                        canBeHidden={true}
                                        onCommit={(val) => commitValue("height", val, {
                                            min: matThickness * 2,
                                            max: 200,
                                            decimals: 1
                                        })} />
                                </Grid>
                            </TabPanel>
                            <TabPanel>
                                <Grid templateColumns={["90px 1fr 130px", "90px 1fr 130px", "90px 220px 130px"]} templateRows="repeat(4, 50px)" rowGap={1} columnGap={6} alignItems="center">
                                    <SliderControl
                                        title="Dividers"
                                        stateHook={dividers}
                                        setStateHook={setDividers}
                                        minValue={0}
                                        maxValue={Math.floor(width / matThickness / 4)}
                                        valueStep={1}
                                        stepperDecimals={0}
                                        localStorageKey="dividers"
                                        sliderMarkCondition={false}
                                        canBeHidden={true}
                                        onCommit={(val) => commitValue("dividers", val, {
                                            min: 0,
                                            max: Math.floor(width / matThickness / 4),
                                            integer: true,
                                            allowZero: true
                                        })} />
                                    <SwitchControl
                                        title="Card Tray"
                                        stateHook={isCardTray}
                                        setStateHook={setIsCardTray}
                                        localStorageKey="isCardTray" />
                                    <SliderControl
                                        title="Tenon Size"
                                        stateHook={segmentLength}
                                        setStateHook={setSegmentLength}
                                        minValue={matThickness * 2}
                                        maxValue={50}
                                        valueStep={1}
                                        localStorageKey="segmentLength"
                                        sliderMarkCondition={matThickness * 2 <= 30}
                                        sliderMarkValue={30}
                                        noStepper={true}
                                        disabled={is3DMode}
                                        onCommit={(val) => commitValue("segmentLength", val, {
                                            min: matThickness * 2,
                                            max: 50,
                                            decimals: 1
                                        })} />
                                    <SliderControl
                                        title="Kerf"
                                        hasTooltip={true}
                                        tooltipLabel="Increasing the kerf makes the joints tighter. Start with the laser's spot size and adjust the value gradually to find the best fit."
                                        stateHook={kerf}
                                        setStateHook={setKerf}
                                        minValue={0}
                                        maxValue={0.2}
                                        valueStep={0.001}
                                        stepperDecimals={3}
                                        localStorageKey="kerf"
                                        unit="mm"
                                        canBeHidden={true}
                                        disabled={is3DMode}
                                        onCommit={(val) => commitValue("kerf", val, {
                                            min: 0,
                                            max: 0.2,
                                            decimals: 3,
                                            allowZero: true
                                        })} />
                                </Grid>
                            </TabPanel>
                            <TabPanel>
                                <Grid templateColumns="1fr auto auto" gap={3} alignItems="center" maxH="200px" overflowY="auto" py={1}>
                                    {presets.map((item) => (
                                        <React.Fragment key={item.name}>
                                            <Box>
                                                <Text fontWeight="semibold">{item.name}</Text>
                                                <HStack spacing={2} mt={1} wrap="wrap">
                                                    {computeTags(item.values).map((tag) => {
                                                        const colorMap = {
                                                            box: "gray",
                                                            cards: "teal",
                                                            flat: "yellow",
                                                            horizontal: "blue",
                                                            vertical: "purple",
                                                            fixed: "orange",
                                                            adjustable: "green"
                                                        };
                                                        const scheme = colorMap[tag] || "gray";
                                                        return <Tag key={tag} size="sm" variant="subtle" colorScheme={scheme}>{tag}</Tag>;
                                                    })}
                                                </HStack>
                                            </Box>
                                            <IconButton
                                                aria-label={`Load ${item.name}`}
                                                icon={<FiDownload />}
                                                colorScheme="blue"
                                                variant="outline"
                                                onClick={() => {
                                                    const { width: exW, depth: exD, height: exH, thickness: exT, dividers: exDiv, isCardTray: exCard, segmentLength: exSeg, kerf: exKerf } = item.values;
                                                    const applyStr = (key, val) => {
                                                        const str = typeof val === "number" ? val.toString() : String(val);
                                                        setters[key]?.(str);
                                                        lastValidRef.current[key] = str;
                                                        localStorage.setItem(key, str);
                                                    };
                                                    applyStr("width", exW);
                                                    applyStr("depth", exD);
                                                    applyStr("height", exH);
                                                    applyStr("matThickness", exT);
                                                    applyStr("dividers", exDiv);
                                                    applyStr("segmentLength", exSeg);
                                                    applyStr("kerf", exKerf);
                                                    setIsCardTray(exCard);
                                                    localStorage.setItem("isCardTray", exCard);
                                                }}
                                            />
                                            <IconButton
                                                aria-label={`Delete ${item.name}`}
                                                icon={<FiTrash2 />}
                                                colorScheme="red"
                                                variant="outline"
                                                onClick={() => {
                                                    const filtered = presets.filter((p) => p.name !== item.name);
                                                    setPresets(filtered);
                                                    localStorage.setItem("boxPresets", JSON.stringify(filtered));
                                                }}
                                            />
                                        </React.Fragment>
                                    ))}
                                    <Input
                                        placeholder="New preset name"
                                        value={newPresetName}
                                        onChange={(e) => {
                                            setNewPresetName(e.target.value);
                                            if (presetNameError) setPresetNameError(false);
                                        }}
                                        isInvalid={presetNameError}
                                    />
                                    <IconButton
                                        aria-label="Add preset"
                                        icon={<FiPlus />}
                                        colorScheme="blue"
                                        variant="outline"
                                        gridColumn="2 / span 2"
                                        w="100%"
                                        onClick={() => {
                                            const trimmed = newPresetName.trim();
                                            if (!trimmed) {
                                                setPresetNameError(true);
                                                return;
                                            }
                                            if (presets.some((p) => p.name === trimmed)) {
                                                setPresetNameError(true);
                                                return;
                                            }
                                            const newPreset = {
                                                name: trimmed,
                                                values: {
                                                    width: numericWidth,
                                                    depth: numericDepth,
                                                    height: numericHeight,
                                                    thickness: numericMatThickness,
                                                    dividers: numericDividers,
                                                    isCardTray,
                                                    segmentLength: numericSegmentLength,
                                                    kerf: numericKerf
                                                }
                                            };
                                            const updated = [...presets, newPreset].sort((a, b) => a.name.localeCompare(b.name));
                                            setPresets(updated);
                                            localStorage.setItem("boxPresets", JSON.stringify(updated));
                                            setNewPresetName("");
                                            setPresetNameError(false);
                                        }}
                                    />
                                </Grid>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </Box>
                <Flex direction="column" boxShadow="lg" borderWidth="1px" borderRadius="lg" p={4} background={panelBg} backdropFilter="auto" backdropBlur="5px" gap={3}>
                    <Stack direction={["column", "row"]} spacing={3} mb={1}>
                        {!is3DMode && <Button
                            colorScheme="blue"
                            leftIcon={<FaPuzzlePiece />}
                            flex={{ base: "unset", md: 1 }}
                            w={{ base: "100%", md: "auto" }}
                            isDisabled={!exportHandlers?.downloadSvg}
                            onClick={exportHandlers?.downloadSvg}
                        >Download SVG</Button>}
                        {is3DMode && <>
                            <Button
                                colorScheme="blue"
                                leftIcon={<FaCube />}
                                flex={{ base: "unset", md: 1 }}
                                w={{ base: "100%", md: "auto" }}
                                isDisabled={!exportHandlers?.exportStl}
                                onClick={exportHandlers?.exportStl}
                            >Download STL{(isCardTray && numericDividers > 0) && " (zipped)"}</Button>
                            <Button
                                colorScheme="blue"
                                leftIcon={<Bambu />}
                                flex={{ base: "unset", md: 1 }}
                                w={{ base: "100%", md: "auto" }}
                                isDisabled={!exportHandlers?.openInBambu}
                                onClick={exportHandlers?.openInBambu}
                            >Download 3MF</Button>
                        </>}
                    </Stack>
                    <Center>
                        {is3DMode ? <Text fontSize="sm" color="gray.500" display="inline-flex" alignItems="center">
                            <Icon as={IoInformationCircleOutline} mr={2} />
                            STL/3MF are for 3D printers. To download SVG, switch to 2D mode.
                        </Text>
                            : <Text fontSize="sm" color="gray.500" display="inline-flex" alignItems="center">
                                <Icon as={IoInformationCircleOutline} mr={2} />
                                SVG is for laser cutters. To download STL/3MF, switch to 3D mode.
                            </Text>
                        }
                    </Center>
                </Flex>
            </Flex>
        </>
    );
};
